// client/src/pages/Admin/ActivityLog/ActivityLog.tsx

import React, { useState } from 'react';
import { Activity, Filter, Calendar, Search, RefreshCw } from 'lucide-react';
import { useActivityLog } from './hooks/useActivityLog';
import { ActivityTable } from './components/ActivityTable';
import { ActivityAction, ActivitySeverity } from './types/activity.types';
import { ACTION_LABELS, SEVERITY_LABELS } from './constants/activity.constants';
import './ActivityLog.css';

export const ActivityLog: React.FC = () => {
    const [search, setSearch] = useState('');
    const [actionFilter, setActionFilter] = useState<string>('');
    const [severityFilter, setSeverityFilter] = useState<string>('');
    const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
        start: '',
        end: '',
    });

    const { activities, loading, total, filter, setFilter, refresh } = useActivityLog({
        search: search || undefined,
        action: actionFilter as ActivityAction || undefined,
        severity: severityFilter as ActivitySeverity || undefined,
    });

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setFilter({ ...filter, search: search || undefined });
    };

    const handleClearFilters = () => {
        setSearch('');
        setActionFilter('');
        setSeverityFilter('');
        setDateRange({ start: '', end: '' });
        setFilter({});
    };

    const actionOptions = Object.entries(ACTION_LABELS).map(([key, label]) => ({
        value: key,
        label,
    }));

    const severityOptions = Object.entries(SEVERITY_LABELS).map(([key, label]) => ({
        value: key,
        label,
    }));

    return (
        <div className="activity-log-container">
            <div className="activity-log-header">
                <div className="header-left">
                    <h2>
                        <Activity size={24} className="header-icon" />
                        Activity Log
                    </h2>
                    <span className="header-badge">{total} activities</span>
                </div>
                <button className="btn-secondary" onClick={refresh}>
                    <RefreshCw size={16} />
                    Refresh
                </button>
            </div>

            {/* Filters */}
            <div className="activity-filters">
                <form onSubmit={handleSearch} className="filter-form">
                    <div className="filter-group">
                        <Search size={16} className="filter-icon" />
                        <input
                            type="text"
                            placeholder="Search activities..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="filter-input"
                        />
                    </div>

                    <div className="filter-group">
                        <select
                            value={actionFilter}
                            onChange={(e) => setActionFilter(e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Actions</option>
                            {actionOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <select
                            value={severityFilter}
                            onChange={(e) => setSeverityFilter(e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Severities</option>
                            {severityOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group date-group">
                        <Calendar size={16} className="filter-icon" />
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                            className="filter-input date-input"
                            placeholder="Start"
                        />
                        <span className="date-separator">—</span>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                            className="filter-input date-input"
                            placeholder="End"
                        />
                    </div>

                    <button type="submit" className="btn-primary filter-submit">
                        <Filter size={16} />
                        Apply
                    </button>

                    {(search || actionFilter || severityFilter || dateRange.start) && (
                        <button
                            type="button"
                            className="btn-secondary filter-clear"
                            onClick={handleClearFilters}
                        >
                            Clear
                        </button>
                    )}
                </form>
            </div>

            {/* Activity Table */}
            <ActivityTable activities={activities} loading={loading} />
        </div>
    );
};

export default ActivityLog;
